// Floor Planner — upload a plan, calibrate the scale, place Makasa packs,
// trace walls, view in isometric 3D, export a printable PDF.
// Mobile-friendly: bottom-sheet library, pinch zoom, joystick walk mode.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Ruler, Box, Layers, FileDown, Save, Trash2, RotateCcw, RotateCw, PenLine, Package, Armchair, Share2, Copy, Mail, Footprints, Orbit, X, Plus, Home, DoorOpen } from "lucide-react";
import EditorCanvas from "./EditorCanvas";
import ThreeView from "./ThreeView";
import { exportProjectPdf } from "./pdf";
import { BRANDS, fmtM, fmtEur, uid, WALL_DEFAULTS } from "./data";
import api, { floorPlannerSave, floorPlannerGet, floorPlannerLead } from "@/lib/api";

const LS_KEY = "floorplanner-project-v1";

function useIsMobile() {
  const [m, setM] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 768 : false));
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const f = () => setM(mq.matches);
    f();
    mq.addEventListener("change", f);
    return () => mq.removeEventListener("change", f);
  }, []);
  return m;
}

/** Virtual joystick for the first-person walk mode on touch screens.
 * Writes {x: strafe, z: forward} in [-1,1] into moveRef. */
function WalkJoystick({ moveRef, brand }) {
  const padRef = useRef(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const R = 40;
  const update = (t) => {
    const rect = padRef.current.getBoundingClientRect();
    let dx = t.clientX - (rect.left + rect.width / 2);
    let dy = t.clientY - (rect.top + rect.height / 2);
    const len = Math.hypot(dx, dy);
    if (len > R) { dx = (dx / len) * R; dy = (dy / len) * R; }
    moveRef.current = { x: dx / R, z: -dy / R };
    setKnob({ x: dx, y: dy });
  };
  const reset = () => { moveRef.current = { x: 0, z: 0 }; setKnob({ x: 0, y: 0 }); };
  return (
    <div ref={padRef} data-testid="walk-joystick"
      className="absolute bottom-6 left-6 w-28 h-28 rounded-full z-10"
      style={{ background: "rgba(30,41,59,0.35)", border: "2px solid rgba(255,255,255,0.5)", touchAction: "none" }}
      onTouchStart={(e) => { e.preventDefault(); update(e.touches[0]); }}
      onTouchMove={(e) => { e.preventDefault(); update(e.touches[0]); }}
      onTouchEnd={reset}
    >
      <div className="absolute w-12 h-12 rounded-full pointer-events-none"
        style={{ background: brand.primary, left: "50%", top: "50%", transform: `translate(calc(-50% + ${knob.x}px), calc(-50% + ${knob.y}px))`, boxShadow: "0 2px 8px rgba(0,0,0,0.4)" }} />
    </div>
  );
}

export default function FloorPlannerPage() {
  const isMobile = useIsMobile();
  const isTouch = useMemo(() => typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches, []);
  const [brand, setBrand] = useState("makasa");
  const [title, setTitle] = useState("Mon studio");
  const [plan, setPlan] = useState(null); // {dataUrl, width, height}
  const [mpp, setMpp] = useState(null);   // meters per image-pixel
  const [items, setItems] = useState([]);
  const [walls, setWalls] = useState([]);
  const [mode, setMode] = useState("2d");
  const [tool, setTool] = useState("select"); // select | calibrate | wall | place
  const [placing, setPlacing] = useState(null);
  const [calibLine, setCalibLine] = useState(null);
  const [calibMeters, setCalibMeters] = useState("");
  const [bgOpacity, setBgOpacity] = useState(0.55);
  const [selected, setSelected] = useState(null);
  const [packs, setPacks] = useState([]);
  const [generic, setGeneric] = useState([]);
  const [openPack, setOpenPack] = useState(null);
  const [libTab, setLibTab] = useState("packs");
  const [savedAt, setSavedAt] = useState(null);
  const [walk, setWalk] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadBusy, setLeadBusy] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [autoScaling, setAutoScaling] = useState(false);
  const [areaInput, setAreaInput] = useState("");
  const [areaUnit, setAreaUnit] = useState("m2"); // m2 | ft2
  const mppRef = useRef(null);
  useEffect(() => { mppRef.current = mpp; }, [mpp]);
  // Multi-room: roomData holds snapshots of INACTIVE rooms; the editor state
  // (plan/mpp/items/walls) always reflects the ACTIVE room.
  const [roomList, setRoomList] = useState([{ id: "r1", name: "Pièce principale" }]);
  const [activeRoom, setActiveRoom] = useState("r1");
  const roomData = useRef({}); // {roomId: {plan, mpp, items, walls}}
  const [openingType, setOpeningType] = useState("door"); // door | window
  const stageRef = useRef(null);
  const threeRef = useRef(null);
  const containerRef = useRef(null);
  const fileRef = useRef(null);
  const moveRef = useRef({ x: 0, z: 0 });

  const B = BRANDS[brand];

  // ── Multi-room helpers ─────────────────────────────────────
  const loadRoomState = (d) => {
    const data = d || {};
    setPlan(data.plan || null); setMpp(data.mpp ?? null);
    setItems(data.items || []); setWalls(data.walls || []);
    setSelected(null); setPlacing(null); setTool("select"); setCalibLine(null);
  };

  const snapshotActive = () => ({ plan, mpp, items, walls });

  const switchRoom = (id) => {
    if (id === activeRoom) return;
    roomData.current[activeRoom] = snapshotActive();
    loadRoomState(roomData.current[id]);
    setActiveRoom(id);
  };

  const addRoom = () => {
    roomData.current[activeRoom] = snapshotActive();
    const id = `r${Date.now().toString(36)}`;
    setRoomList((l) => [...l, { id, name: `Pièce ${l.length + 1}` }]);
    roomData.current[id] = {};
    loadRoomState({});
    setActiveRoom(id);
    toast.success("Nouvelle pièce — chargez son plan");
  };

  const deleteRoom = (id) => {
    if (roomList.length <= 1) { toast.error("Il faut au moins une pièce"); return; }
    const remaining = roomList.filter((r) => r.id !== id);
    setRoomList(remaining);
    delete roomData.current[id];
    if (id === activeRoom) {
      loadRoomState(roomData.current[remaining[0].id]);
      setActiveRoom(remaining[0].id);
    }
    toast("Pièce supprimée");
  };

  const renameRoom = (id) => {
    const cur = roomList.find((r) => r.id === id);
    const name = window.prompt("Nom de la pièce :", cur?.name || "");
    if (name?.trim()) setRoomList((l) => l.map((r) => (r.id === id ? { ...r, name: name.trim().slice(0, 40) } : r)));
  };

  // committed list of ALL rooms incl. the active one (for save/share/export)
  const allRooms = () => {
    const data = { ...roomData.current, [activeRoom]: snapshotActive() };
    return roomList.map((r) => ({ id: r.id, name: r.name, plan: data[r.id]?.plan || null, mpp: data[r.id]?.mpp ?? null, items: data[r.id]?.items || [], walls: data[r.id]?.walls || [] }));
  };

  const hydrateRooms = (rooms) => {
    const list = [];
    const data = {};
    rooms.forEach((r, i) => {
      const id = `r${i + 1}`;
      const m = r.mpp || null;
      list.push({ id, name: r.name || `Pièce ${i + 1}` });
      data[id] = {
        plan: r.plan ? { dataUrl: r.plan.url || r.plan.dataUrl, width: r.plan.width, height: r.plan.height } : null,
        mpp: m,
        items: (r.items || []).map((it) => (m ? { ...it, wPx: it.width_m / m, dPx: it.depth_m / m } : it)),
        walls: r.walls || [],
      };
    });
    roomData.current = data;
    setRoomList(list);
    setActiveRoom(list[0].id);
    loadRoomState(data[list[0].id]);
  };

  useEffect(() => {
    api.get("/floor-planner/packs").then((r) => {
      setPacks(r.data.packs || []);
      setGeneric(r.data.generic_items || []);
    }).catch(() => toast.error("Impossible de charger les packs"));
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        setBrand(p.brand || "makasa"); setTitle(p.title || "Mon studio");
        if (p.rooms?.length) {
          hydrateRooms(p.rooms);
        } else {
          setPlan(p.plan || null); setMpp(p.mpp || null);
          setItems(p.items || []); setWalls(p.walls || []);
        }
        setSavedAt(p.savedAt || null);
      }
    } catch { /* fresh start */ }
    // Shared project link (?p=CODE) overrides local state
    const code = new URLSearchParams(window.location.search).get("p");
    if (code) {
      floorPlannerGet(code).then((p) => {
        setBrand(p.brand || "makasa"); setTitle(p.title || "Studio partagé");
        if (p.rooms?.length) {
          hydrateRooms(p.rooms);
        } else {
          const m = p.mpp || null;
          setPlan(p.plan ? { dataUrl: p.plan.url || p.plan.dataUrl, width: p.plan.width, height: p.plan.height } : null);
          setMpp(m);
          setItems((p.items || []).map((it) => (m ? { ...it, wPx: it.width_m / m, dPx: it.depth_m / m } : it)));
          setWalls(p.walls || []);
        }
        toast.success("Projet partagé chargé");
      }).catch(() => toast.error("Lien de partage invalide ou expiré"));
    }
  }, []);

  const save = useCallback((silent) => {
    try {
      const savedAt = new Date().toISOString();
      const rooms = allRooms().map(({ id, name, plan: rp, mpp: rm, items: ri, walls: rw }) => ({ id, name, plan: rp, mpp: rm, items: ri, walls: rw }));
      localStorage.setItem(LS_KEY, JSON.stringify({ brand, title, rooms, activeRoom, savedAt }));
      setSavedAt(savedAt);
      if (!silent) toast.success("Projet sauvegardé dans ce navigateur");
    } catch {
      toast.error("Sauvegarde impossible — le plan est peut-être trop lourd pour le navigateur");
    }
  });

  // AI scale detection — reads cotations/doors so users can skip calibration
  const detectScale = async (p) => {
    setAutoScaling(true);
    const t = toast.loading("Détection automatique de l'échelle…");
    try {
      const r = await api.post("/floor-planner/auto-scale", { dataUrl: p.dataUrl, width: p.width, height: p.height });
      toast.dismiss(t);
      if (mppRef.current) return; // user calibrated manually while detection ran — keep theirs
      if (r.data?.mpp && r.data.confidence !== "low") {
        setMpp(r.data.mpp);
        setTool("select");
        toast.success(`Échelle détectée : ~${String(r.data.width_m).replace(".", ",")} m de large (${r.data.basis}) — ajustez via Calibrer si besoin`);
      } else {
        toast.info("Échelle incertaine — tracez une cote connue ou entrez la surface");
        setTool("calibrate");
      }
    } catch {
      toast.dismiss(t);
      toast.info("Détection impossible — tracez une cote connue ou entrez la surface");
      setTool("calibrate");
    } finally { setAutoScaling(false); }
  };

  // Scale from the room/home surface (m² or ft²) — assumes the plan fills the image
  const applySurfaceScale = () => {
    const area = parseFloat(String(areaInput).replace(",", "."));
    if (!area || area <= 0 || !plan) { toast.error("Entrez la surface totale"); return; }
    const m2 = areaUnit === "ft2" ? area * 0.09290304 : area;
    setMpp(Math.sqrt(m2 / (plan.width * plan.height)));
    setAreaInput("");
    setTool("select");
    toast.success(`Échelle déduite de ${area} ${areaUnit === "ft2" ? "ft²" : "m²"} — vérifiez avec un meuble de taille connue`);
  };

  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const im = new window.Image();
      im.onload = () => {
        const p = { dataUrl: reader.result, width: im.width, height: im.height };
        setPlan(p);
        setMpp(null); setItems([]); setWalls([]);
        detectScale(p);
      };
      im.src = reader.result;
    };
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const calibLenPx = calibLine ? Math.hypot(calibLine.pts[2] - calibLine.pts[0], calibLine.pts[3] - calibLine.pts[1]) : 0;
  const applyCalibration = () => {
    const meters = parseFloat(String(calibMeters).replace(",", "."));
    if (!meters || meters <= 0 || calibLenPx < 10) { toast.error("Tracez une ligne puis entrez sa longueur réelle"); return; }
    setMpp(meters / calibLenPx);
    setCalibLine(null); setCalibMeters(""); setTool("select");
    toast.success(`Échelle calibrée : 1 m = ${Math.round(calibLenPx / meters)} px`);
  };

  const px = useCallback((m) => (mpp ? m / mpp : m * 100), [mpp]); // fallback 100 px/m before calibration

  const placeItem = (lib, pt, packId) => {
    const wPx = px(lib.width_m), dPx = px(lib.depth_m);
    setItems((arr) => [...arr, {
      uid: uid(), ref: lib.id, name: lib.name, x: pt.x, y: pt.y, rot: 0,
      width_m: lib.width_m, depth_m: lib.depth_m, height_m: lib.height_m,
      color: lib.color, round: !!lib.round, category: lib.category,
      wPx, dPx, packId: packId || lib.packId || null, img: lib.img || null, model: lib.model || null,
    }]);
  };

  const onPlace = (pt) => {
    if (!placing) return;
    placeItem(placing, pt, placing.packId);
    setPlacing(null); setTool("select");
  };

  // Pick a furniture item from the library: on mobile, close the sheet and
  // make sure the user is on the 2D canvas where the placement tap happens.
  const pickItem = (it, packId) => {
    if (!mpp) { toast.error("Calibrez l'échelle d'abord"); return; }
    setPlacing(packId ? { ...it, packId } : it);
    setTool("place");
    setMode("2d");
    setSheetOpen(false);
    toast.info(`Touchez le plan pour placer : ${it.name}`);
  };

  const placeWholePack = (pack) => {
    if (!plan) { toast.error("Chargez d'abord un plan"); return; }
    if (!mpp) { toast.error("Calibrez l'échelle d'abord"); return; }
    const cx = plan.width / 2, cy = plan.height / 2;
    const cols = Math.ceil(Math.sqrt(pack.items.length));
    const gap = px(0.4);
    pack.items.forEach((lib, i) => {
      const row = Math.floor(i / cols), col = i % cols;
      for (let q = 0; q < (lib.qty || 1); q++) {
        placeItem({ ...lib, packId: pack.pack_id }, {
          x: cx + (col - cols / 2) * (px(lib.width_m) + gap) + q * px(0.55),
          y: cy + (row - cols / 2) * (px(lib.depth_m) + gap),
        }, pack.pack_id);
      }
    });
    setMode("2d"); setSheetOpen(false);
    toast.success(`${pack.name} placé — déplacez les meubles à votre goût`);
  };

  const deleteSelected = useCallback(() => {
    setItems((arr) => arr.filter((a) => a.uid !== selected));
    setWalls((arr) => arr
      .map((w) => ({ ...w, openings: (w.openings || []).filter((o) => `op-${o.id}` !== selected) }))
      .filter((w) => w.id !== selected));
    setSelected(null);
  }, [selected]);

  const rotateSelected = () =>
    setItems((arr) => arr.map((a) => (a.uid === selected ? { ...a, rot: ((a.rot || 0) + 45) % 360 } : a)));

  useEffect(() => {
    const onKey = (e) => {
      if ((e.key === "Delete" || e.key === "Backspace") && selected) deleteSelected();
      if (e.key === "Escape") { setTool("select"); setPlacing(null); setSelected(null); setCalibLine(null); setSheetOpen(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, deleteSelected]);

  const planAreaM2 = plan && mpp ? plan.width * mpp * (plan.height * mpp) : null;

  // Draw a room's 2D snapshot offscreen (plan image + walls + furniture photos)
  // so multi-room PDF export never has to flip the on-screen editor state.
  const snapshotRoom2d = async (room) => {
    if (!room.plan?.dataUrl) return null;
    const load = (src) => new Promise((res) => {
      const im = new window.Image();
      im.onload = () => res(im);
      im.onerror = () => res(null);
      im.src = src;
    });
    const planImg = await load(room.plan.dataUrl);
    if (!planImg) return null;
    const maxW = 1400;
    const scale = Math.min(1, maxW / room.plan.width);
    const cv = document.createElement("canvas");
    cv.width = Math.round(room.plan.width * scale);
    cv.height = Math.round(room.plan.height * scale);
    const cx = cv.getContext("2d");
    cx.fillStyle = "#FFFFFF";
    cx.fillRect(0, 0, cv.width, cv.height);
    cx.scale(scale, scale);
    cx.globalAlpha = 0.55;
    cx.drawImage(planImg, 0, 0);
    cx.globalAlpha = 1;
    const rm = room.mpp;
    cx.strokeStyle = "#1E293B";
    cx.lineWidth = rm ? 0.15 / rm : 10;
    cx.lineCap = "round"; cx.lineJoin = "round";
    (room.walls || []).forEach((w) => {
      cx.beginPath();
      for (let i = 0; i + 1 < w.points.length; i += 2) i === 0 ? cx.moveTo(w.points[0], w.points[1]) : cx.lineTo(w.points[i], w.points[i + 1]);
      cx.stroke();
    });
    for (const it of room.items || []) {
      const wPx = rm ? it.width_m / rm : it.width_m * 100;
      const dPx = rm ? it.depth_m / rm : it.depth_m * 100;
      cx.save();
      cx.translate(it.x, it.y);
      cx.rotate(((it.rot || 0) * Math.PI) / 180);
      cx.fillStyle = it.color || "#999";
      cx.globalAlpha = 0.35;
      if (it.round) { cx.beginPath(); cx.arc(0, 0, wPx / 2, 0, Math.PI * 2); cx.fill(); }
      else cx.fillRect(-wPx / 2, -dPx / 2, wPx, dPx);
      cx.globalAlpha = 1;
      const photo = await load(it.img || imgByRef[it.ref] || "");
      if (photo) {
        const bw = wPx * 0.84, bh = dPx * 0.84;
        const ir = photo.width / photo.height;
        let dw = bw, dh = bw / ir;
        if (dh > bh) { dh = bh; dw = bh * ir; }
        cx.drawImage(photo, -dw / 2, -dh / 2, dw, dh);
      }
      cx.restore();
    }
    return cv.toDataURL("image/jpeg", 0.85);
  };

  // item ref (e.g. "es-sofa") → product photo / 3D model URL; covers projects saved before these existed
  const imgByRef = useMemo(() => {
    const m = {};
    packs.forEach((p) => (p.items || []).forEach((it) => { if (it.img) m[it.id] = it.img; }));
    generic.forEach((it) => { if (it.img) m[it.id] = it.img; });
    return m;
  }, [packs, generic]);
  const modelByRef = useMemo(() => {
    const m = {};
    packs.forEach((p) => (p.items || []).forEach((it) => { if (it.model) m[it.id] = it.model; }));
    return m;
  }, [packs]);

  const runExport = async () => {
    // JPEG at capped width keeps the PDF light enough to email/print.
    const toJpeg = (dataUrl, maxW = 1400, q = 0.82) => new Promise((resolve) => {
      const im = new window.Image();
      im.onload = () => {
        const scale = Math.min(1, maxW / im.width);
        const cv = document.createElement("canvas");
        cv.width = Math.round(im.width * scale);
        cv.height = Math.round(im.height * scale);
        const cx = cv.getContext("2d");
        cx.fillStyle = "#FFFFFF";
        cx.fillRect(0, 0, cv.width, cv.height);
        cx.drawImage(im, 0, 0, cv.width, cv.height);
        resolve(cv.toDataURL("image/jpeg", q));
      };
      im.onerror = () => resolve(null);
      im.src = dataUrl;
    });
    if (walk) setWalk(false);
    const roomsAll = allRooms();
    const roomShots = [];
    for (const r of roomsAll) {
      const image2d = await snapshotRoom2d(r);
      roomShots.push({
        name: r.name, mpp: r.mpp,
        planAreaM2: r.plan && r.mpp ? r.plan.width * r.mpp * (r.plan.height * r.mpp) : null,
        image2d, image3d: null, items: r.items,
      });
    }
    // 3D snapshot of the room currently on screen (needs the calibrated active room)
    if (mpp && plan) {
      if (mode !== "3d") {
        setMode("3d");
        await new Promise((r) => setTimeout(r, 1600)); // let the 3D models settle for the snapshot
      }
      const raw3d = threeRef.current?.capture?.();
      const image3d = raw3d ? await toJpeg(raw3d) : null;
      const active = roomShots[roomList.findIndex((r) => r.id === activeRoom)];
      if (active) active.image3d = image3d;
    }
    exportProjectPdf({ brand: B, title, packs, rooms: roomShots });
    toast.success("PDF généré — prêt à imprimer");
  };

  const exportPdf = () => {
    if (sessionStorage.getItem("fp-lead-done") === "1") { runExport(); return; }
    setShowLead(true);
  };

  const submitLead = async () => {
    const email = leadEmail.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("Entrez un email valide"); return; }
    setLeadBusy(true);
    try {
      await floorPlannerLead({
        email, name: leadName.trim(), brand, title,
        packs: [...new Set(items.map((i) => i.packId).filter(Boolean))],
        surface_m2: planAreaM2 ? Number(planAreaM2.toFixed(1)) : null,
      });
      sessionStorage.setItem("fp-lead-done", "1");
      setShowLead(false);
      toast.success("Merci ! Votre PDF se prépare…");
      runExport();
    } catch { toast.error("Envoi impossible — réessayez"); }
    finally { setLeadBusy(false); }
  };

  const shareProject = async () => {
    setSharing(true);
    try {
      const roomsPayload = allRooms().map((r) => ({
        name: r.name, mpp: r.mpp, items: r.items, walls: r.walls,
        plan: r.plan
          ? (r.plan.dataUrl?.startsWith("data:")
              ? { dataUrl: r.plan.dataUrl, width: r.plan.width, height: r.plan.height }
              : { url: r.plan.dataUrl, width: r.plan.width, height: r.plan.height })
          : null,
      }));
      const r = await floorPlannerSave({ brand, title, rooms: roomsPayload });
      // Build the link from the current host so preview/staging links stay valid
      setShareUrl(r.code ? `${window.location.origin}/tools/floor-planner?p=${r.code}` : r.url);
      toast.success("Lien de partage créé");
    } catch { toast.error("Partage impossible — réessayez"); }
    finally { setSharing(false); }
  };

  const btn = (active) =>
    `inline-flex items-center gap-1.5 px-2 sm:px-3 py-2 text-xs font-semibold border transition rounded-none flex-shrink-0 ${
      active ? "text-white" : "hover:opacity-80"}`;

  const toolbarBtnStyle = (active) => ({
    background: active ? B.primary : "transparent",
    borderColor: B.border, color: active ? "#fff" : B.text,
  });

  const step = !plan ? 1 : !mpp ? 2 : items.length ? 4 : 3;

  // ── Reusable panels (rendered once: desktop rail OR mobile sheet) ──
  const uploadCard = (
    <div className="p-6 text-center my-auto w-full max-w-sm mx-auto">
      <Upload className="w-8 h-8 mx-auto mb-3" style={{ color: B.primary }} />
      <p className="font-display text-lg mb-1">Chargez votre plan</p>
      <p className="text-xs mb-4" style={{ color: B.muted }}>
        Photo ou scan du plan (JPG/PNG). L'échelle est détectée automatiquement par IA — ou entrez simplement la surface. Ensuite placez le mobilier et imprimez en PDF.
      </p>
      <button onClick={() => fileRef.current?.click()} data-testid="floorplan-upload-button"
        className="px-4 py-3 text-sm font-semibold text-white w-full" style={{ background: B.primary }}>
        Choisir un fichier
      </button>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="floorplan-upload-input" onChange={onUpload} />
    </div>
  );

  const libraryPanel = (
    <>
      <div className="flex border-b text-xs font-semibold flex-shrink-0" style={{ borderColor: B.border }}>
        {[["packs", "Packs Makasa"], ["items", "Mobilier"], ["projet", "Projet"]].map(([k, l]) => (
          <button key={k} onClick={() => setLibTab(k)} data-testid={`lib-tab-${k}`}
            className="flex-1 py-3 transition"
            style={{ color: libTab === k ? B.primary : B.muted, borderBottom: libTab === k ? `2px solid ${B.primary}` : "2px solid transparent" }}>
            {l}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {libTab === "packs" && packs.map((p) => (
          <div key={p.pack_id} className="border" style={{ borderColor: B.border }} data-testid="pack-card">
            <button className="w-full text-left p-3" onClick={() => setOpenPack(openPack === p.pack_id ? null : p.pack_id)}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm flex items-center gap-2"><Package className="w-4 h-4" style={{ color: B.primary }} />{p.name}</span>
                <span className="text-xs font-bold" style={{ color: B.primary }}>{fmtEur(p.price_eur)}</span>
              </div>
              <p className="text-[11px] mt-0.5" style={{ color: B.muted }}>{p.style} · {p.items.length} meubles à placer</p>
            </button>
            {openPack === p.pack_id && (
              <div className="px-3 pb-3">
                <p className="text-[11px] mb-2" style={{ color: B.muted }}>{p.description}</p>
                <div className="space-y-1 mb-3">
                  {p.items.map((it) => (
                    <button key={it.id} data-testid="furniture-item-drag-source"
                      onClick={() => pickItem(it, p.pack_id)}
                      className="w-full flex items-center justify-between text-left px-2 py-2.5 text-xs hover:opacity-75 transition"
                      style={{ background: B.subtle }}>
                      <span className="flex items-center gap-2">
                        {it.img
                          ? <img src={it.img} alt="" loading="lazy" className="w-9 h-9 object-contain flex-shrink-0 rounded-sm" style={{ background: "#fff" }} />
                          : <span className="inline-block w-3 h-3 rounded-sm" style={{ background: it.color }} />}
                        {it.name}{it.qty > 1 ? ` ×${it.qty}` : ""}
                      </span>
                      <span className="font-mono text-[10px]" style={{ color: B.muted }}>{fmtM(it.width_m)}×{fmtM(it.depth_m)}</span>
                    </button>
                  ))}
                </div>
                <button data-testid="place-pack-button" onClick={() => placeWholePack(p)}
                  className="w-full py-2.5 text-xs font-semibold text-white" style={{ background: B.primary }}>
                  Placer tout le pack
                </button>
              </div>
            )}
          </div>
        ))}
        {libTab === "items" && generic.map((it) => (
          <button key={it.id} data-testid="furniture-item-drag-source"
            onClick={() => pickItem(it)}
            className="w-full flex items-center justify-between text-left px-3 py-2.5 text-xs border hover:opacity-75 transition"
            style={{ borderColor: B.border }}>
            <span className="flex items-center gap-2"><Armchair className="w-3.5 h-3.5" style={{ color: B.primary }} />{it.name}</span>
            <span className="font-mono text-[10px]" style={{ color: B.muted }}>{fmtM(it.width_m)}×{fmtM(it.depth_m)}</span>
          </button>
        ))}
        {libTab === "projet" && (
          <div className="text-xs space-y-2 p-1" data-testid="project-summary">
            <p><span style={{ color: B.muted }}>Surface (est.) :</span> <strong>{planAreaM2 ? `${planAreaM2.toFixed(1).replace(".", ",")} m²` : "calibrez l'échelle"}</strong></p>
            <p><span style={{ color: B.muted }}>Meubles placés :</span> <strong>{items.length}</strong></p>
            <p><span style={{ color: B.muted }}>Murs tracés :</span> <strong>{walls.length}</strong></p>
            <p><span style={{ color: B.muted }}>Packs utilisés :</span> <strong>{[...new Set(items.map((i) => i.packId).filter(Boolean))].length}</strong></p>
            <div className="pt-2 border-t" style={{ borderColor: B.border }}>
              <label className="block mb-1" style={{ color: B.muted }}>Opacité du plan : {Math.round(bgOpacity * 100)}%</label>
              <input type="range" min="0.15" max="1" step="0.05" value={bgOpacity} onChange={(e) => setBgOpacity(Number(e.target.value))} className="w-full" data-testid="bg-opacity-slider" />
            </div>
            <p className="pt-2" style={{ color: B.muted }}>
              {isTouch
                ? "Astuce : touchez un meuble pour le déplacer, pincez pour zoomer, glissez le fond pour déplacer la vue."
                : <>Astuce : touche <kbd>Suppr</kbd> pour retirer la sélection, double-clic pour terminer un mur, molette pour zoomer.</>}
            </p>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="h-dvh flex flex-col overflow-hidden" style={{ background: B.bg, color: B.text }} data-testid="floor-planner-tool-page">
      {/* ── Toolbar ─────────────────────────────────────────── */}
      <header className="min-h-14 flex items-center gap-1.5 sm:gap-2 px-3 border-b flex-shrink-0 overflow-x-auto" style={{ background: B.surface, borderColor: B.border }}>
        <button
          data-testid="brand-theme-toggle"
          onClick={() => setBrand(brand === "makasa" ? "julius" : "makasa")}
          className="flex items-center gap-2 pr-3 border-r mr-1 flex-shrink-0" style={{ borderColor: B.border }}
          title="Changer de marque"
        >
          <span className="font-display text-base sm:text-lg leading-none" style={{ color: B.primary }}>{B.name}</span>
        </button>
        <input
          value={title} onChange={(e) => setTitle(e.target.value)}
          className="bg-transparent text-sm font-medium w-24 md:w-36 outline-none border-b border-transparent focus:border-current flex-shrink-0 hidden sm:block"
          style={{ color: B.text }} data-testid="project-title-input"
        />
        <span className="text-[10px] ml-1 hidden md:inline flex-shrink-0" style={{ color: B.muted }} data-testid="save-status">
          {savedAt ? `Sauvegardé ${new Date(savedAt).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : "Non sauvegardé"}
        </span>

        <div className="flex-1 min-w-2" />

        <button data-testid="view-mode-2d-button" className={btn(mode === "2d")} style={toolbarBtnStyle(mode === "2d")} onClick={() => setMode("2d")}>
          <Layers className="w-3.5 h-3.5" /> <span className="hidden sm:inline">2D</span>
        </button>
        <button data-testid="view-mode-3d-button" className={btn(mode === "3d")} style={toolbarBtnStyle(mode === "3d")}
          onClick={() => { if (!mpp) { toast.error("Calibrez l'échelle d'abord"); return; } setMode("3d"); }}>
          <Box className="w-3.5 h-3.5" /> <span className="hidden sm:inline">3D</span>
        </button>
        <button data-testid="calibrate-scale-button" className={btn(tool === "calibrate")} style={toolbarBtnStyle(tool === "calibrate")}
          onClick={() => setTool("calibrate")} title="Calibrer l'échelle">
          <Ruler className="w-3.5 h-3.5" /> <span className="hidden md:inline">{mpp ? `1 m = ${Math.round(1 / mpp)} px` : "Calibrer"}</span>
        </button>
        <button data-testid="wall-tool-button" className={btn(tool === "wall")} style={toolbarBtnStyle(tool === "wall")}
          onClick={() => setTool("wall")} title="Tracer des murs (double-clic pour terminer)">
          <PenLine className="w-3.5 h-3.5" /> <span className="hidden md:inline">Murs</span>
        </button>
        <button data-testid="openings-tool-button" className={btn(tool === "opening")} style={toolbarBtnStyle(tool === "opening")}
          onClick={() => setTool("opening")} title="Placer des portes et fenêtres dans les murs">
          <DoorOpen className="w-3.5 h-3.5" /> <span className="hidden md:inline">Ouvertures</span>
        </button>

        <div className="flex-1 min-w-2" />

        <button data-testid="save-project-button" className={btn(false)} style={toolbarBtnStyle(false)} onClick={() => save()} title="Sauvegarder">
          <Save className="w-3.5 h-3.5" /> <span className="hidden md:inline">Sauver</span>
        </button>
        {plan && (
          <button data-testid="share-project-button" className={btn(false)} style={toolbarBtnStyle(false)}
            onClick={shareProject} disabled={sharing} title="Partager par lien">
            <Share2 className="w-3.5 h-3.5" /> <span className="hidden md:inline">{sharing ? "…" : "Partager"}</span>
          </button>
        )}
        <button data-testid="clear-canvas-button" className={`${btn(false)} hidden sm:inline-flex`} style={toolbarBtnStyle(false)}
          onClick={() => { setItems([]); setWalls([]); setSelected(null); toast("Canvas vidé"); }} title="Vider le canvas">
          <RotateCcw className="w-3.5 h-3.5" /> <span className="hidden md:inline">Vider</span>
        </button>
        <button data-testid="export-pdf-report-button"
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white flex-shrink-0"
          style={{ background: B.accent }} onClick={exportPdf}>
          <FileDown className="w-3.5 h-3.5" /> PDF
        </button>
      </header>

      {/* ── Step indicator — guides first-time users through the flow ── */}
      <div className="flex items-center gap-3 px-3 py-1.5 border-b overflow-x-auto flex-shrink-0"
           style={{ background: B.subtle, borderColor: B.border }} data-testid="step-indicator">
        {["Charger le plan", "Calibrer (auto)", "Placer les meubles", "Exporter le PDF"].map((l, i) => {
          const n = i + 1, done = n < step, active = n === step;
          return (
            <span key={n} className="flex items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold" data-testid={`step-${n}`}
                  style={{ color: done ? B.primary : active ? B.text : B.muted }}>
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] flex-shrink-0"
                    style={{ background: done ? B.primary : active ? B.text : B.border, color: "#fff" }}>
                {done ? "✓" : n}
              </span>
              {l}
            </span>
          );
        })}
      </div>

      {/* ── Room switcher (multi-room projects) ────────────── */}
      <div className="flex items-center gap-1.5 px-3 py-1.5 border-b overflow-x-auto flex-shrink-0"
           style={{ background: B.surface, borderColor: B.border }} data-testid="room-switcher">
        <Home className="w-3.5 h-3.5 flex-shrink-0" style={{ color: B.muted }} />
        {roomList.map((r) => (
          <span key={r.id} className="flex items-center flex-shrink-0">
            <button onClick={() => switchRoom(r.id)} onDoubleClick={() => renameRoom(r.id)}
              title="Double-clic pour renommer" data-testid={`room-tab-${r.id}`}
              className="px-2.5 py-1 text-[11px] font-semibold border transition"
              style={{ background: activeRoom === r.id ? B.primary : "transparent",
                       color: activeRoom === r.id ? "#fff" : B.text, borderColor: B.border }}>
              {r.name}
            </button>
            {roomList.length > 1 && (
              <button onClick={() => deleteRoom(r.id)} className="px-1 text-[10px]" style={{ color: B.muted }}
                title="Supprimer cette pièce" data-testid={`room-delete-${r.id}`}>✕</button>
            )}
          </span>
        ))}
        <button onClick={addRoom} data-testid="add-room-button"
          className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold border flex-shrink-0"
          style={{ borderColor: B.border, color: B.primary }}>
          <Plus className="w-3 h-3" /> Pièce
        </button>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* ── Library rail (desktop) ────────────────────────── */}
        {!isMobile && (
          <aside className="w-80 flex-shrink-0 border-r flex flex-col" style={{ background: B.surface, borderColor: B.border }}>
            {!plan ? uploadCard : libraryPanel}
          </aside>
        )}

        {/* ── Canvas workspace ──────────────────────────────── */}
        <main ref={containerRef} className="flex-1 min-w-0 relative">
          {!plan ? (
            isMobile ? (
              <div className="absolute inset-0 overflow-y-auto flex">{uploadCard}</div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-sm" style={{ color: B.muted }}>Le plan apparaîtra ici</p>
              </div>
            )
          ) : mode === "2d" ? (
            <EditorCanvas
              plan={plan} mpp={mpp} items={items} setItems={setItems} walls={walls} setWalls={setWalls}
              tool={tool} placing={placing} onPlace={onPlace} calibLine={calibLine} setCalibLine={setCalibLine}
              openingType={openingType}
              bgOpacity={bgOpacity} selected={selected} setSelected={setSelected}
              stageRef={stageRef} containerRef={containerRef} brand={B} imgByRef={imgByRef}
            />
          ) : (
            <>
              <ThreeView
                ref={threeRef}
                plan={{ dataUrl: plan.dataUrl, w_m: plan.width * mpp, h_m: plan.height * mpp }}
                items={items.map((it) => ({ ...it, model: it.model || modelByRef[it.ref] || null, x_m: it.x * mpp, y_m: it.y * mpp }))}
                walls={walls.map((w) => ({ id: w.id, points_m: w.points.map((v) => v * mpp),
                  openings: (w.openings || []).map((o) => ({ seg: o.seg, off_m: o.off * mpp, width_m: o.wPx * mpp, type: o.type })) }))}
                walk={walk}
                moveRef={moveRef}
              />
              <div className="absolute top-3 left-1/2 -translate-x-1/2 flex shadow-lg" data-testid="walk-mode-toggle">
                <button onClick={() => setWalk(false)} data-testid="orbit-mode-button"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition"
                  style={{ background: walk ? B.surface : B.primary, color: walk ? B.text : "#fff", border: `1px solid ${B.border}` }}>
                  <Orbit className="w-3.5 h-3.5" /> Orbite
                </button>
                <button onClick={() => setWalk(true)} data-testid="walk-mode-button"
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition"
                  style={{ background: walk ? B.primary : B.surface, color: walk ? "#fff" : B.text, border: `1px solid ${B.border}` }}>
                  <Footprints className="w-3.5 h-3.5" /> À pied
                </button>
              </div>
              {walk && (
                <>
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.85)", boxShadow: "0 0 4px rgba(0,0,0,0.5)" }} />
                  </div>
                  <div className={`absolute ${isTouch ? "bottom-40" : "bottom-4"} left-1/2 -translate-x-1/2 px-4 py-1.5 text-[11px] font-medium text-center max-w-[92vw] pointer-events-none`}
                       style={{ background: "rgba(30,41,59,0.85)", color: "#fff" }} data-testid="walk-hint">
                    {isTouch ? "Joystick pour marcher · glissez pour regarder autour de vous" : "Cliquez pour capturer la souris (Échap pour sortir) · ZQSD / WASD pour marcher"}
                  </div>
                  {isTouch && <WalkJoystick moveRef={moveRef} brand={B} />}
                </>
              )}
            </>
          )}

          {/* Mobile: floating button opens the furniture library sheet */}
          {/* raised above the toast zone so toasts never cover it */}
          {isMobile && plan && !sheetOpen && !walk && (
            <button data-testid="open-library-fab" onClick={() => setSheetOpen(true)}
              className="absolute bottom-24 right-4 z-20 flex items-center gap-2 px-4 py-3 rounded-full text-sm font-semibold text-white shadow-xl"
              style={{ background: B.primary }}>
              <Package className="w-5 h-5" /> Mobilier
            </button>
          )}

          {/* Touch: action bar for the selected item/wall */}
          {plan && mode === "2d" && selected && isTouch && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 z-20" data-testid="selection-action-bar">
              {items.some((i) => i.uid === selected) && (
                <button onClick={rotateSelected} data-testid="selection-rotate-button"
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-white shadow-lg"
                  style={{ background: B.primary }}>
                  <RotateCw className="w-4 h-4" /> Pivoter
                </button>
              )}
              <button onClick={deleteSelected} data-testid="selection-delete-button"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-white shadow-lg"
                style={{ background: "#DC2626" }}>
                <Trash2 className="w-4 h-4" /> Supprimer
              </button>
              <button onClick={() => setSelected(null)} data-testid="selection-done-button"
                className="px-4 py-2.5 text-xs font-semibold shadow-lg"
                style={{ background: B.surface, color: B.text, border: `1px solid ${B.border}` }}>
                OK
              </button>
            </div>
          )}

          {plan && mode === "2d" && tool === "calibrate" && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 px-4 py-2 text-xs font-medium shadow-lg max-w-[92vw] pointer-events-none"
                 style={{ background: "#3B82F6", color: "#fff" }} data-testid="calibration-modal">
              {calibLine ? (
                <span className="flex items-center gap-2 flex-wrap justify-center pointer-events-auto">
                  Ligne : {Math.round(calibLenPx)} px — longueur réelle :
                  <input value={calibMeters} onChange={(e) => setCalibMeters(e.target.value)} placeholder="ex. 3,50"
                         inputMode="decimal"
                         className="w-16 px-1.5 py-0.5 text-black rounded-sm" data-testid="calibration-meters-input"
                         onKeyDown={(e) => e.key === "Enter" && applyCalibration()} autoFocus />
                  m
                  <button onClick={applyCalibration} className="px-2 py-0.5 bg-white text-blue-600 font-bold rounded-sm" data-testid="calibration-apply-button">OK</button>
                  <button onClick={() => { setCalibLine(null); setTool("select"); }} className="opacity-80" data-testid="calibration-cancel-button">✕</button>
                </span>
              ) : (
                <span className="flex items-center gap-2 flex-wrap justify-center">
                  <span>{isTouch ? "Touchez-glissez le long d'une cote connue," : "Tracez une ligne sur une cote connue,"} ou entrez la surface :</span>
                  <span className="flex items-center gap-1.5 pointer-events-auto">
                    <input value={areaInput} onChange={(e) => setAreaInput(e.target.value)} placeholder="ex. 24"
                           inputMode="decimal" data-testid="surface-area-input"
                           className="w-16 px-1.5 py-0.5 text-black rounded-sm"
                           onKeyDown={(e) => e.key === "Enter" && applySurfaceScale()} />
                    <button onClick={() => setAreaUnit(areaUnit === "m2" ? "ft2" : "m2")} data-testid="surface-unit-toggle"
                      className="px-1.5 py-0.5 bg-white/20 font-bold rounded-sm" title="Basculer m² / ft²">
                      {areaUnit === "m2" ? "m²" : "ft²"}
                    </button>
                    <button onClick={applySurfaceScale} className="px-2 py-0.5 bg-white text-blue-600 font-bold rounded-sm" data-testid="surface-apply-button">OK</button>
                  </span>
                </span>
              )}
            </div>
          )}
          {plan && mode === "2d" && tool === "wall" && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 px-4 py-2 text-xs font-medium shadow-lg max-w-[92vw] text-center pointer-events-none"
                 style={{ background: "#1E293B", color: "#fff" }}>
              {isTouch
                ? `Touchez pour tracer les murs — double-tap pour terminer (hauteur ${WALL_DEFAULTS.height_m} m en 3D)`
                : `Cliquez pour tracer les murs — double-clic pour terminer (hauteur ${WALL_DEFAULTS.height_m} m en 3D)`}
            </div>
          )}
          {plan && mode === "2d" && tool === "opening" && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 px-4 py-2 text-xs font-medium shadow-lg max-w-[92vw] pointer-events-none"
                 style={{ background: "#6D28D9", color: "#fff" }} data-testid="openings-banner">
              {!mpp ? (
                "Calibrez l'échelle d'abord — les portes et fenêtres ont des dimensions réelles"
              ) : (
                <span className="flex items-center gap-2 flex-wrap justify-center">
                  Touchez un mur pour placer :
                  <button onClick={() => setOpeningType("door")} data-testid="opening-type-door"
                    className="px-2.5 py-1 font-bold rounded-sm pointer-events-auto"
                    style={{ background: openingType === "door" ? "#fff" : "rgba(255,255,255,0.2)", color: openingType === "door" ? "#6D28D9" : "#fff" }}>
                    Porte
                  </button>
                  <button onClick={() => setOpeningType("window")} data-testid="opening-type-window"
                    className="px-2.5 py-1 font-bold rounded-sm pointer-events-auto"
                    style={{ background: openingType === "window" ? "#fff" : "rgba(255,255,255,0.2)", color: openingType === "window" ? "#6D28D9" : "#fff" }}>
                    Fenêtre
                  </button>
                  — touchez une ouverture pour la sélectionner
                </span>
              )}
            </div>
          )}
          {autoScaling && (
            <div className="absolute top-3 left-3 px-3 py-1.5 text-[11px] font-semibold text-white shadow-lg z-10 animate-pulse"
                 style={{ background: B.primary }} data-testid="auto-scale-status">
              Détection de l'échelle par IA…
            </div>
          )}
          {plan && !mpp && !autoScaling && mode === "2d" && tool !== "calibrate" && (
            <button onClick={() => setTool("calibrate")}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 text-xs font-bold text-white shadow-lg animate-pulse"
              style={{ background: B.accent }} data-testid="calibrate-nudge">
              Étape suivante : calibrer l'échelle
            </button>
          )}
        </main>
      </div>

      {/* Mobile bottom sheet: furniture library */}
      {isMobile && plan && sheetOpen && (
        <div className="fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSheetOpen(false)} data-testid="library-sheet-backdrop" />
          <div className="absolute inset-x-0 bottom-0 max-h-[75vh] flex flex-col shadow-2xl"
               style={{ background: B.surface, borderTop: `2px solid ${B.border}` }} data-testid="library-bottom-sheet">
            <div className="flex items-center justify-between px-4 py-2 border-b flex-shrink-0" style={{ borderColor: B.border }}>
              <span className="font-semibold text-sm">Mobilier & packs</span>
              <button onClick={() => setSheetOpen(false)} data-testid="close-library-sheet" className="p-2" style={{ color: B.muted }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            {libraryPanel}
          </div>
        </div>
      )}

      {shareUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShareUrl(null)}>
          <div className="p-6 w-[26rem] max-w-[92vw] shadow-2xl" style={{ background: B.surface, border: `1px solid ${B.border}` }}
               onClick={(e) => e.stopPropagation()} data-testid="share-modal">
            <h3 className="font-display text-xl mb-1" style={{ color: B.text }}>Partager ce plan</h3>
            <p className="text-xs mb-4" style={{ color: B.muted }}>
              Ce lien rouvre le plan exact — mobilier, échelle et murs inclus. Envoyez-le au client.
            </p>
            <div className="flex gap-2 mb-4">
              <input readOnly value={shareUrl} onFocus={(e) => e.target.select()} data-testid="share-link-input"
                className="flex-1 px-3 py-2 text-xs outline-none" style={{ background: B.subtle, border: `1px solid ${B.border}`, color: B.text }} />
              <button onClick={() => { navigator.clipboard?.writeText(shareUrl); toast.success("Lien copié"); }}
                data-testid="share-copy-button" className="px-3 py-2 text-white" style={{ background: B.primary }}>
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <a data-testid="share-email-button"
                href={`mailto:?subject=${encodeURIComponent("Plan 3D de votre studio — " + title)}&body=${encodeURIComponent(`Bonjour,\n\nVoici le plan meublé de votre studio :\n${shareUrl}\n\nOuvrez-le pour le faire pivoter en 3D ou imprimer le rapport PDF.\n`)}`}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white" style={{ background: B.accent }}>
                <Mail className="w-4 h-4" /> Envoyer par email
              </a>
              <button onClick={() => setShareUrl(null)} className="text-xs underline" style={{ color: B.muted }} data-testid="share-close-button">Fermer</button>
            </div>
          </div>
        </div>
      )}

      {showLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="p-6 w-[24rem] max-w-[92vw] shadow-2xl" style={{ background: B.surface, border: `1px solid ${B.border}` }} data-testid="lead-capture-modal">
            <h3 className="font-display text-xl mb-1" style={{ color: B.text }}>Votre rapport est prêt</h3>
            <p className="text-xs mb-4" style={{ color: B.muted }}>
              {brand === "makasa"
                ? "Laissez votre email : Makasa vous envoie le devis exact du pack choisi et reste disponible pour l'installation."
                : "Laissez votre email pour recevoir ce rapport et la suite de nos conseils aménagement."}
            </p>
            <input value={leadName} onChange={(e) => setLeadName(e.target.value)} placeholder="Prénom (facultatif)"
              className="w-full px-3 py-2.5 text-sm mb-2 outline-none" style={{ background: B.subtle, border: `1px solid ${B.border}`, color: B.text }}
              data-testid="lead-name-input" />
            <input value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)} placeholder="votre@email.fr" type="email"
              className="w-full px-3 py-2.5 text-sm mb-4 outline-none" style={{ background: B.subtle, border: `1px solid ${B.border}`, color: B.text }}
              data-testid="lead-email-input" onKeyDown={(e) => e.key === "Enter" && submitLead()} />
            <button onClick={submitLead} disabled={leadBusy} data-testid="lead-submit-button"
              className="w-full py-2.5 text-sm font-semibold text-white" style={{ background: B.primary }}>
              {leadBusy ? "Envoi…" : "Recevoir mon PDF"}
            </button>
            <p className="text-[10px] mt-3 text-center" style={{ color: B.muted }}>
              Votre email sert uniquement à l'envoi du rapport et du devis — aucune publicité.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
